import {
  MultiSelect,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { UseFormReturn, useWatch } from "react-hook-form";

import DateInput from "@/components/shared/forms/DateInput";
import { MultiSelectToggleButton } from "@/components/shared/forms/MultiSelectToggleButton";
import { MultiSelectClearButton } from "@/components/shared/forms/MultiSelectClearButton";
import { multiSelectTagRender } from "@/components/shared/forms/multiSelectTagRender";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import RadioFieldRow from "@/components/shared/forms/RadioFieldRow";
import RadioSelect from "@/components/shared/forms/RadioSelect";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import { CHLD, CHLD_TEMPLATE } from "@/models/chld.tsx";
import { useCoreData } from "@/queries.ts";

import { FaBriefcase } from "react-icons/fa";
import { FiUser } from "react-icons/fi";
import { useCHLDFormLogic } from "./useCHLDFormLogic";
import { validateMinDate } from "@/utils/util";
import { SSIDWarning } from "./SSIDWarning";
import { useMultiSelectChipListLabel } from "@/components/shared/forms/useMultiSelectChipListLabel";
import { useMultiSelectListboxMultiselectable } from "@/components/shared/forms/useMultiSelectListboxMultiselectable";
import { multiSelectSelectedItemRender } from "@/components/shared/forms/multiSelectSelectedItemRender";

type Option = {
  Text: string;
  Value: string;
};

type MultiSelectFieldProps = {
  id?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  disabled: boolean;
  options: Option[];
  initialValues: string[];
  onChangeValues: (values: string[]) => void;
  maxSelections: number;
  placeholder: string;
  resetKey: string;
  errorMessage?: string;
  afterChange?: () => void;
};

function MultiSelectField({
  id,
  ariaLabel,
  ariaDescribedBy,
  disabled,
  options,
  initialValues,
  onChangeValues,
  maxSelections,
  placeholder,
  resetKey,
  errorMessage,
  afterChange,
}: MultiSelectFieldProps) {
  const [selected, setSelected] = useState<Option[]>(() => {
    const initialSet = initialValues.filter(Boolean);
    return initialSet
      .map((val) => options.find((o) => o.Value === val))
      .filter((o): o is Option => !!o)
      .slice(0, maxSelections);
  });

  useEffect(() => {
    const initialSet = initialValues.filter(Boolean);
    const mapped = initialSet
      .map((val) => options.find((o) => o.Value === val))
      .filter((o): o is Option => !!o)
      .slice(0, maxSelections);

    setSelected(mapped);
  }, [resetKey, initialValues, options, maxSelections]);

  const handleChange = (e: MultiSelectChangeEvent) => {
    const next = (e.value as Option[]) ?? [];
    const limited = next.slice(0, maxSelections);

    setSelected(limited);
    onChangeValues(limited.map((o) => o.Value));
    afterChange?.();
  };

  useMultiSelectChipListLabel(id, ariaLabel, selected);

  // See MultiSelectToggleButton: gives keyboard users a way to browse the
  // option list without having to type first.
  const [opened, setOpened] = useState(false);

  useMultiSelectListboxMultiselectable(id, opened);

  return (
    <MultiSelect
      id={id}
      ariaLabel={ariaLabel}
      ariaDescribedBy={ariaDescribedBy}
      data={options}
      textField="Text"
      dataItemKey="Value"
      value={selected}
      onChange={handleChange}
      disabled={disabled}
      style={{ width: "100%" }}
      placeholder={placeholder}
      valid={!errorMessage}
      validationMessage={errorMessage}
      opened={opened}
      onOpen={() => setOpened(true)}
      onClose={() => setOpened(false)}
      itemRender={multiSelectSelectedItemRender}
      popupSettings={id ? { className: `${id}-listbox-popup` } : undefined}
      tagRender={multiSelectTagRender}
      suffix={
        <>
          <MultiSelectToggleButton
            opened={opened}
            onToggle={() => setOpened((prev) => !prev)}
            disabled={disabled}
          />
          {selected.length > 0 && (
            <MultiSelectClearButton
              onClear={() =>
                handleChange({ value: [] } as MultiSelectChangeEvent)
              }
              disabled={disabled}
            />
          )}
        </>
      }
    />
  );
}

export function CHLDAddEditForm({
  selectedChild,
  form,
  childDefaults,
}: {
  selectedChild: CHLD | undefined;
  form: UseFormReturn<CHLD, any, CHLD>;
  childDefaults?: Partial<CHLD>;
}) {
  const params = useParams({
    shouldThrow: false,
    from: "/$leaid/_error-wrapper/records/child/$entityid",
  });

  const leaContext = useRouteContext({ from: "/$leaid" });
  const viewOnly = !leaContext.hasRole("CHLDEdit");
  const coreData = useCoreData();
  const tooltipMap = leaContext.coreData.tooltipMap.get("CHLD");

  if (!coreData) {
    return <div>Loading...</div>;
  }

  const defaultPreschoolCode =
    childDefaults?.PreschoolCode ??
    leaContext.currentOrgPreschools[0]?.PreschoolCDSCode ??
    "";

  const newChildTemplate: CHLD = {
    ...CHLD_TEMPLATE,
    ...childDefaults,
    CSPPID: childDefaults?.CSPPID ?? params?.entityid ?? "",
    PreschoolCode: defaultPreschoolCode,
    EffectiveStartDate: "",
    EffectiveEndDate: "",
  };

  const child = selectedChild ?? newChildTemplate;

  const {
    control,
    register,
    setValue,
    clearErrors,
    formState: { errors },
  } = form;

  const { getRaces, setRaces, validateEffectiveStartDate, trigger } =
    useCHLDFormLogic(form, child);

  const resetKey = selectedChild?.CSPPID || `new:${params?.entityid ?? ""}`;

  const programYear = useWatch({ control, name: "ProgramYear" });

  const ssidValue = useWatch({ control, name: "SSID" });
  const iepIfspValue = useWatch({ control, name: "IEPIFSPIndicator" });

  const childCountryOfBirth = useWatch({
    control,
    name: "ChildCountryOfBirth",
    defaultValue: child.ChildCountryOfBirth,
  });

  const stateOptions = (() => {
    const filtered = coreData.states.filter(
      (x) => x.CountryCode === childCountryOfBirth
    );

    if (childCountryOfBirth !== "US") return filtered;

    const i = filtered.findIndex((x) => x.Name === "California");
    if (i <= 0) return filtered;

    return [filtered[i], ...filtered.slice(0, i), ...filtered.slice(i + 1)];
  })();

  const isDomesticCountry =
    childCountryOfBirth === "US" ||
    childCountryOfBirth === "CA" ||
    childCountryOfBirth === "MX";

  useEffect(() => {
    clearErrors(["ChildStateOfBirth", "ChildCityOfBirth"]);

    if (!isDomesticCountry) {
      setValue("ChildStateOfBirth", "", {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [childCountryOfBirth, isDomesticCountry, clearErrors, setValue]);

  return (
    <Container
      className="form-style overview-container ps-3 pe-3"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      {/* PROGRAM INFORMATION CARD */}
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaBriefcase className="section-icon" />
          <h5 className="section-title">Agency Information</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="CSPPID"
              labelText="CSPPID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!child.CSPPID}
                  maxLength={50}
                  {...register(name, { required: "CSPPID is required" })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="EffectiveStartDate"
              labelText="Effective Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  max="9999-12-31"
                  disabled={viewOnly}
                  {...register(name, {
                    validate: validateEffectiveStartDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedChild?.ProgramYear && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Program Year is required" }}
                    options={coreData.programYearList}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, {
                      required: "Program Year is required",
                    })}
                    options={coreData.programYearList}
                  />
                )
              }
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, { required: "Preschool is required" })}
                  options={leaContext
                    .currentOrgPreschoolsByProgramYear(programYear)
                    .map((x) => ({
                      Text: x.PreschoolName,
                      Value: x.PreschoolCDSCode,
                    }))}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="LocalChildId"
              labelText="Local Child ID (CICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!child.LocalChildId}
                  maxLength={50}
                  {...register(name, {
                    required: "Local Child ID (CICN) is required",
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="SSID"
              labelText="SSID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={10}
                  {...register(name, {
                    validate: (value: string | undefined) =>
                      !value || value.length === 10
                        ? true
                        : "SSID must be exactly 10 characters",
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      {/* CHILD INFORMATION CARD */}
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FiUser className="section-icon" />
          <h5 className="section-title">Child Information</h5>
        </div>

        <div className="row g-3">
          {/* Row 1: First / Middle / Last */}
          <div className="col-md-4">
            <FormFieldRow
              name="ChildFirstName"
              labelText="First Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={30}
                  {...register(name, { required: "First name is required" })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildMiddleName"
              labelText="Middle Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={50}
                  {...register(name)}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildLastName"
              labelText="Last Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={30}
                  {...register(name, { required: "Last name is required" })}
                />
              )}
            </FormFieldRow>
          </div>

          {/* Row 2: Suffix / Country / State */}
          <div className="col-md-4">
            <FormFieldRow
              name="ChildNameSuffix"
              labelText="Suffix"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name)}
                  options={coreData.nameSuffixList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildCountryOfBirth"
              labelText="Country of Birth"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, {
                    required: "Country of birth is required",
                  })}
                  options={coreData.countryList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildStateOfBirth"
              labelText="State of Birth"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={isDomesticCountry}
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, {
                    validate: (value: string | undefined) => {
                      if (isDomesticCountry)
                        return value ? true : "State of birth is required";
                      return value
                        ? "State of birth must be empty for the selected country"
                        : true;
                    },
                  })}
                  options={stateOptions.map((x) => ({
                    Text: x.Name,
                    Value: x.Code,
                  }))}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildCityOfBirth"
              labelText="City of Birth"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={isDomesticCountry}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={30}
                  {...register(name, {
                    validate: (value: string | undefined) => {
                      if (isDomesticCountry)
                        return value ? true : "City of birth is required";
                      return true;
                    },
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildDateOfBirth"
              labelText="Date of Birth"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(name, {
                    required: "Date of birth is required",
                    validate: validateMinDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildGender"
              labelText="Gender"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, { required: "Gender is required" })}
                  options={coreData.genderList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <RadioFieldRow
              name="ChildEthnicity"
              labelText="Hispanic"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <RadioSelect
                  id={id}
                  disabled={viewOnly}
                  {...register(id, { required: "Hispanic field is required" })}
                  options={[
                    { Text: "Yes", Value: "Y" },
                    { Text: "No", Value: "N" },
                    { Text: "No Response", Value: "" },
                  ]}
                />
              )}
            </RadioFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildRace1"
              labelText="Race(s)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <div>
                  <input type="hidden" {...register("ChildRace1")} />
                  <input type="hidden" {...register("ChildRace2")} />
                  <input type="hidden" {...register("ChildRace3")} />
                  <input type="hidden" {...register("ChildRace4")} />
                  <input type="hidden" {...register("ChildRace5")} />
                  <input
                    type="hidden"
                    {...register("ChildEthnicityMissingIndicator")}
                  />
                  <input
                    type="hidden"
                    {...register("ChildRaceMissingIndicator")}
                  />
                  <MultiSelectField
                    id={id}
                    ariaLabel="Race(s)"
                    ariaDescribedBy={`${id}-description`}
                    key={`${resetKey}:races`}
                    disabled={viewOnly}
                    resetKey={resetKey}
                    options={coreData.raceList}
                    initialValues={getRaces()}
                    maxSelections={5}
                    placeholder="Select up to five races"
                    onChangeValues={setRaces}
                    afterChange={() => trigger("ChildRace1")}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="IEPIFSPIndicator"
              labelText="Active IEP or IFSP?"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, {
                    required: "Active IEP or IFSP is required",
                  })}
                  options={coreData.IEPorIFSPList}
                />
              )}
            </FormFieldRow>
            <SSIDWarning iepValue={iepIfspValue} ssidValue={ssidValue} />
          </div>
        </div>
      </div>

      <span
        id="spnAddOrEditClassroomErrorOverall"
        className="row ps-3 mt-3 pe-3 float-end"
        style={{ color: "#CC0000" }}
      ></span>
    </Container>
  );
}
